/* @flow */

import { isUndefinedOrNull } from '../jsHelpers/types';
import { StorageKeys } from '../localStorage/keys';
import LocalStorageManager from '../localStorage/localStorageManager';
import ProfilesManager from '../profiles/profilesManager';
import { getDefaultValueFromSetting } from './defaultValues';
import { Setting, type Settings } from './types';

// Restore all settings to their default values and store them into local storage
const resetSettings: (skipSave?: boolean) => Settings = (skipSave) => {
  const settings = initializeUndefinedSettings({});

  if (!skipSave) {
    saveSettings(settings);
  }

  return settings;
};

const initializeUndefinedSettings: (settings: Settings) => Settings = (settings) => {
  Array.from(Setting.members()).forEach((setting) => {
    const { [setting]: settingValue } = settings;
    if (isUndefinedOrNull(settingValue)) {
      settings[setting] = getDefaultValueFromSetting(setting);
    }
  });

  return settings;
};

// Load settings from local storage
const loadSettings: () => Settings = () => {
  const settings = LocalStorageManager.loadObject(ProfilesManager.buildCurrentProfileKey(StorageKeys.Settings));

  if (!settings) {
    // No settings stored yet
    return resetSettings();
  }

  return initializeUndefinedSettings(settings);
};

const loadSettingsForProfile: (profileId: string) => Settings = (profileId) => {
  const settings: Settings | null = profileId ? LocalStorageManager.loadObject(ProfilesManager.buildProfileKey(StorageKeys.Settings, profileId)) : null;

  if (!settings) {
    // No settings stored yet (only save default one in profile edit mode (i.e. profileId !== undefined)
    return resetSettings(profileId !== undefined);
  }

  return initializeUndefinedSettings(settings);
};

// Save settings into local storage
const saveSettings: (settings: Settings, profileId?: string) => void = (settings, profileId) => {
  LocalStorageManager.save(ProfilesManager.buildProfileKey(StorageKeys.Settings, profileId), settings);
};

const deleteLocalSettingsForProfile: (profileId: string) => void = (profileId) => {
  LocalStorageManager.delete(ProfilesManager.buildProfileKey(StorageKeys.Settings, profileId));
};

const getBooleanSetting = (settings: Settings, name: Setting): boolean => {
  const value = settings[name];

  if (typeof value === 'boolean') {
    return value;
  }

  return getDefaultValueFromSetting(name) as any as boolean;
};

const getNumberSetting = (settings: Settings, name: Setting): number => {
  const value = settings[name];

  if (typeof value === 'number') {
    return value;
  }

  return getDefaultValueFromSetting(name) as any as number;
};

const getStringSetting = (settings: Settings, name: Setting): string => {
  const value = settings[name];

  if (typeof value === 'string') {
    return value;
  }

  return getDefaultValueFromSetting(name) as any as string;
};

export { deleteLocalSettingsForProfile, getBooleanSetting, getNumberSetting, getStringSetting, loadSettings, loadSettingsForProfile, resetSettings, saveSettings };
